'use client';

import React, { useState, useMemo } from 'react';
import { MaterialReactTable } from 'material-react-table';
import { Box, Typography, Paper, Button, IconButton, Switch, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { orderBy } from 'lodash';
import { Download, Delete, Restore } from '@mui/icons-material';
import TitleBar from '../layout/TitleBar';
import { useNavigate } from 'react-router-dom';
import { useAuthData } from '@/hooks/getAuthData';
import { useGoas, useUpdateGoaAsset } from '@/hooks/api/useGoas';
import { useLocations } from '@/hooks/useLocation';
import { useNotification } from '@/contexts/NotificationContext';

const GoasPage: React.FC = () => {
  const navigate = useNavigate();
  const { roles } = useAuthData();
  const hasAdminRole = roles.some((x) => x.name === 'Admin');
  const { data: goasData, isLoading: goasLoading } = useGoas();
  const { data: locationsData } = useLocations();
  const updateGoaAssetMutation = useUpdateGoaAsset();
  const { showSuccess, showError } = useNotification();

  const [showInactive, setShowInactive] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [assetToDelete, setAssetToDelete] = useState<any>(null);

  const allGoas = useMemo(() => {
    if (!goasData) return [];
    return Array.isArray(goasData) ? goasData : Object.values(goasData);
  }, [goasData]);

  // Filter out inactive assets by default
  const goas = useMemo(() => {
    if (showInactive) {
      return allGoas;
    }
    return allGoas.filter((goa: any) => goa.active !== false);
  }, [allGoas, showInactive]);

  const locations = useMemo(() => {
    if (!locationsData) return [];
    const locs = Array.isArray(locationsData) ? locationsData : Object.values(locationsData);
    return orderBy(locs, ['name'], ['asc']);
  }, [locationsData]);
  // Generate Location Filter Options
  const locationOptions = locations.map((location: any) => ({
    value: location.id.toString(),
    label: location.name,
  }));

  const handleDelete = (goa: any) => {
    setAssetToDelete(goa);
    setDeleteConfirmOpen(true);
  };

  const handleReactivate = (goa: any) => {
    setAssetToDelete(goa);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (assetToDelete) {
      // Check if asset is inactive (could be false, null, or undefined)
      const isReactivating = assetToDelete.active === false || assetToDelete.active === null;
      const newActiveValue = isReactivating ? true : false;
      
      updateGoaAssetMutation.mutate(
        { id: assetToDelete.id, active: newActiveValue },
        {
          onSuccess: () => {
            showSuccess(isReactivating ? 'GOA reactivated successfully' : 'GOA deactivated successfully');
            setDeleteConfirmOpen(false);
            setAssetToDelete(null);
          },
          onError: (error: any) => {
            showError(error.message || 'Failed to update GOA');
          },
        }
      );
    }
  };

  // Column visibility state with localStorage persistence
  const getStoredColumnVisibility = () => {
    const stored = localStorage.getItem('goasPage_columnVisibility');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  const saveColumnVisibility = (visibility: Record<string, boolean>) => {
    localStorage.setItem('goasPage_columnVisibility', JSON.stringify(visibility));
  };

  const defaultColumnVisibility = {
    assetId: true,
    locationId: true,
    active: false,
    id: false,
    createdAt: false,
    updatedAt: false,
  };

  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() => {
    const stored = getStoredColumnVisibility();
    return stored || defaultColumnVisibility;
  });

  const goaColumns = [
    { accessorKey: 'assetId', header: 'GOA ID',
      Cell: ({ row }: any) => {
        return <span style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }} onClick={() => navigate(`/app/goas/${row.original.id}`)}>{row.original.assetId || row.original.id}</span>
      },
     },
    
    {
      accessorKey: 'locationId', header: 'Location',
      Cell: ({ row }: any) => {
        const location = locations.find((location) => location.id === row.original.locationId);
        return <span style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }} onClick={() => navigate(`/app/locations/${row.original.locationId}`)}>{location?.name || 'No name'}</span>
      },
      filterVariant: 'select', // Enables a dropdown filter
      filterSelectOptions: locationOptions, // Provide the dropdown options
    },
    {
      accessorKey: 'active', header: 'Active',
      Cell: ({ row }: any) => {
        return row.original.active !== false ? 'Yes' : 'No';
      },
      enableHiding: true,
    },
    {
      accessorKey: 'id', header: 'ID',
      Cell: ({ row }: any) => row.original.id,
      enableHiding: true,
    },
    {
      accessorKey: 'createdAt', header: 'Created At',
      Cell: ({ row }: any) => row.original.createdAt ? new Date(row.original.createdAt).toLocaleDateString() : '-',
      enableHiding: true,
    },
    {
      accessorKey: 'updatedAt', header: 'Updated At',
      Cell: ({ row }: any) => row.original.updatedAt ? new Date(row.original.updatedAt).toLocaleDateString() : '-',
      enableHiding: true,
    },
    ...(hasAdminRole ? [{
      id: 'actions',
      header: 'Actions',
      Cell: ({ row }: any) => {
        const isInactive = row.original.active === false || row.original.active === null;
        return isInactive ? (
          <IconButton
            size="small"
            onClick={() => handleReactivate(row.original)}
            color="primary"
            title="Reactivate"
          >
            <Restore />
          </IconButton>
        ) : (
          <IconButton
            size="small"
            onClick={() => handleDelete(row.original)}
            color="error"
            title="Delete"
          >
            <Delete />
          </IconButton>
        );
      },
    }] : []),
  ];


  const breadcrumbs = [
    { label: 'Home', href: '/app/dashboard' },
    { label: 'GOAs' }, // No href for the current page
  ];

  const rightContent = (
    <>
      <Button
        variant="outlined"
        startIcon={<Download />}
        sx={{ textTransform: 'none' }}
      >
        Export
      </Button>
      <Button onClick={() => {
      }} variant="contained" sx={{ textTransform: 'none' }}>
        + Add GOA
      </Button>
    </>
  );

  return (
    <>
      <TitleBar
        breadcrumbs={breadcrumbs}
        title="GOAs"
        rightContent={rightContent}
      />

      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <FormControlLabel
          control={
            <Switch
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
          }
          label="Show Inactive"
        />
      </Box>

      <MaterialReactTable 
        columns={goaColumns as any} 
        data={goas} 
        initialState={{ 
          density: 'compact',
          columnVisibility: columnVisibility
        }}
        state={{
          columnVisibility: columnVisibility
        }}
        onColumnVisibilityChange={(updater) => {
          const newVisibility = typeof updater === 'function' ? updater(columnVisibility) : updater;
          setColumnVisibility(newVisibility);
          saveColumnVisibility(newVisibility);
        }}
      />

      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>
          {(assetToDelete?.active === false || assetToDelete?.active === null) ? 'Confirm Reactivate' : 'Confirm Delete'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {(assetToDelete?.active === false || assetToDelete?.active === null) ? (
              <>Are you sure you want to reactivate GOA <strong>{assetToDelete?.assetId || assetToDelete?.id}</strong>?</>
            ) : (
              <>Are you sure you want to delete GOA <strong>{assetToDelete?.assetId || assetToDelete?.id}</strong>? 
              This will set it as inactive.</>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button 
            onClick={confirmDelete} 
            variant="contained" 
            color={(assetToDelete?.active === false || assetToDelete?.active === null) ? "primary" : "error"}
          >
            {(assetToDelete?.active === false || assetToDelete?.active === null) ? 'Reactivate' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GoasPage;
